import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../../../constants/axiosInstance';

interface SensorId {
  _id: string;
  parentSensor: string | null;
  sensorName: string;
  sensorTag: string;
  sensorNickName: string;
  plantId: string;
  dataInputType: string;
  isEnabled: boolean;
  sensorTypeAbr: string;
  equipmentId: string;
  sensorDetails: string;
  __v: number;
  createdAt: string;
  updatedAt: string;
}

interface SensorConfigDetail {
  modbusMisfireTotalizer: boolean;
  referenceSensorPurpose: string | undefined;
  referenceSensor: { sensorTag: string; _id: string; name: string; } | undefined;
  _id: string;
  dataLoggerId: string;
  sensorId: SensorId;
  sensorType: string;
  scalingFactor?: number;
  start?: number;
  end?: number;
  type?: string;
  wordOrder?: string;
  function?: string;
  bitIndex?: number;
  version: number;
  __v: number;
  sensor: {
    sensorType: string;
    sensorTag: string;
    _id: string;
    name: string;
  };
}

interface AssetId {
  _id: string;
  plantName: string;
}

interface Asset {
  _id: string;
  plantName: string;
}

export interface RemoteControlEquipment {
  equipmentName: string;
  equipmentId: string;
  bidirectionalId: string;
  isNewEntry?: boolean;
  isDeleted?: boolean;
}

export interface DataLoggerDetails {
  _id: string;
  name: string;
  serialNumber: string;
  createdBy: string;
  version: number;
  debug: boolean;
  plcBrand: string;
  pollingInterval: number;
  plcModel: string;
  assetId: AssetId;
  modbusHost: string;
  modbusPort: number;
  unitIdReal: number;
  startAddressReal: number;
  registerCountReal: number;
  unitIdBool: number;
  startAddressBool: number;
  registerCountBool: number;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
  asset: Asset;
  sensorConfig: SensorConfigDetail[];
  remoteControlEquipments?: RemoteControlEquipment[];
}

interface DataLoggerDetailsResponse {
  status: boolean;
  data: DataLoggerDetails;
}

const fetchDataLoggerDetails = async (id?: string): Promise<DataLoggerDetails> => {
  if (!id) throw new Error('Data Logger ID is required');
  const response = await axiosInstance().post<DataLoggerDetailsResponse>('/data-logger/getDataLoggerDetails', {
    dataLoggerId: id
  });
  return response.data.data;
};

export const useDataLoggerDetails = (id?: string) => {
  return useQuery({
    queryKey: ['dataLoggerDetails', id],
    queryFn: () => fetchDataLoggerDetails(id),
    enabled: Boolean(id),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}; 